import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  CircularProgress,
  IconButton,
  LinearProgress,
  MenuItem,
  TextField,
  Tooltip,
} from '@mui/material';
import {
  MdFilterListOff,
  MdRefresh,
  MdTranslate,
} from 'react-icons/md';
import { Page } from '@components/layout/Page';
import { PageEmpty } from '@components/layout/PageEmpty';
import { DataPagination } from '@components/ui/DataPagination';
import { GridSkeleton } from '@components/ui/GridSkeleton';
import { TabFilter } from '@components/ui/TabFilter';
import { UiStringFormDialog } from '@features/uiStrings/components/UiStringFormDialog';
import { UiStringsGrid } from '@features/uiStrings/components/UiStringsGrid';
import { useUiStrings } from '@features/uiStrings/hooks';
import {
  type ListUiStringsParams,
  type SortDirection,
  type UiString,
  type UiStringSortKey,
} from '@features/uiStrings/schema';

const DEFAULT_PAGE_SIZE = 50;

type ActiveTabValue = '' | 'true' | 'false';

const ACTIVE_TABS: ReadonlyArray<{ value: ActiveTabValue; label: string }> = [
  { value: '', label: 'All' },
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
];

export default function UiStringsScreen() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [searchInput, setSearchInput] = useState('');
  const [searchText, setSearchText] = useState('');
  const [namespaceInput, setNamespaceInput] = useState('');
  const [namespace, setNamespace] = useState('');
  const [activeTab, setActiveTab] = useState<ActiveTabValue>('');
  const [sort, setSort] = useState<{
    sortBy?: UiStringSortKey;
    sortDir?: SortDirection;
  }>({});

  // Debounce both text inputs.
  useEffect(() => {
    const t = setTimeout(() => setSearchText(searchInput.trim()), 250);
    return () => clearTimeout(t);
  }, [searchInput]);
  useEffect(() => {
    const t = setTimeout(() => setNamespace(namespaceInput.trim()), 250);
    return () => clearTimeout(t);
  }, [namespaceInput]);

  // Reset to page 1 whenever filters / sort change.
  useEffect(() => {
    setPage(1);
  }, [searchText, namespace, activeTab, pageSize, sort]);

  const params = useMemo<ListUiStringsParams>(
    () => ({
      page,
      pageSize,
      searchText: searchText || undefined,
      namespace: namespace || undefined,
      isActive: activeTab || undefined,
      sortBy: sort.sortBy,
      sortDir: sort.sortDir,
    }),
    [page, pageSize, searchText, namespace, activeTab, sort],
  );

  const { data, isLoading, isFetching, refetch } = useUiStrings(params);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<UiString | null>(null);

  const openEdit = (row: UiString) => {
    setEditing(row);
    setDialogOpen(true);
  };
  const closeDialog = () => {
    setDialogOpen(false);
    setEditing(null);
  };

  const totalRecords = data?.paginationDetails?.totalRecords ?? 0;
  const hasActiveFilters = Boolean(searchText || namespace || activeTab);
  const isInitialLoad = isLoading && !data;
  const showEmpty = !isLoading && (data?.rowData?.length ?? 0) === 0;

  const clearFilters = () => {
    setSearchInput('');
    setSearchText('');
    setNamespaceInput('');
    setNamespace('');
    setActiveTab('');
  };

  return (
    <Page
      title="UI strings"
      subtitle="Mobile-app text resolved per locale. Changes propagate to mobile within 60 seconds."
      fullHeight
      headerRightSlot={
        <TabFilter
          options={ACTIVE_TABS}
          current={activeTab}
          onChange={setActiveTab}
          ariaLabel="Filter by active state"
        />
      }
    >
      <div className="mb-3 flex flex-col gap-3 rounded-xl bg-surface-muted px-3 py-3 md:flex-row md:flex-wrap md:items-end md:px-4">
        <TextField
          label="Search key, value, or description"
          size="small"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="w-full md:min-w-[260px] md:flex-1"
        />
        <TextField
          label="Namespace"
          size="small"
          value={namespaceInput}
          onChange={(e) => setNamespaceInput(e.target.value)}
          placeholder="e.g. price_detail"
          className="w-full md:w-[180px]"
        />
        <div className="flex items-end gap-2 md:contents">
          <TextField
            label="Page size"
            size="small"
            select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="flex-1 md:w-[110px] md:flex-initial"
          >
            {[25, 50, 100].map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </TextField>

          <div className="flex items-center gap-2 md:ml-auto">
            <Tooltip title={isFetching ? 'Loading…' : 'Refresh'}>
              <span>
                <IconButton onClick={() => refetch()} size="small" disabled={isFetching}>
                  {isFetching ? <CircularProgress size={16} thickness={5} /> : <MdRefresh />}
                </IconButton>
              </span>
            </Tooltip>
          </div>
        </div>
      </div>

      <div className="relative flex min-h-0 flex-1 overflow-hidden rounded-xl border border-border bg-surface">
        {isFetching && data && (
          <LinearProgress
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 5,
              height: 2,
            }}
          />
        )}
        {isInitialLoad ? (
          <GridSkeleton
            rows={6}
            columns={[
              { width: '1.4fr', bar: 'w-2/3' },
              { width: '130px', bar: 'w-14' },
              { width: '1.4fr', bar: 'w-3/4' },
              { width: '1.4fr', bar: 'w-3/4' },
              { width: '1.4fr', bar: 'w-3/4' },
              { width: '120px', bar: 'w-14' },
              { width: '200px', bar: 'w-24' },
              { width: '100px', empty: true },
            ]}
          />
        ) : showEmpty ? (
          hasActiveFilters ? (
            <PageEmpty
              icon={MdFilterListOff}
              title="No matches"
              message="Nothing matches the current filters. Try widening your search."
              action={
                <Button variant="outlined" size="small" onClick={clearFilters}>
                  Clear filters
                </Button>
              }
            />
          ) : (
            <PageEmpty
              icon={MdTranslate}
              title="No UI strings yet"
              message="UI strings come from trendz-db migrations. If you expect strings here, ask a developer to confirm the seed migration ran."
            />
          )
        ) : (
          <UiStringsGrid data={data} onEdit={openEdit} onSortChange={setSort} />
        )}
      </div>

      <DataPagination
        page={page}
        pageSize={pageSize}
        totalRecords={totalRecords}
        onPageChange={setPage}
      />

      <UiStringFormDialog open={dialogOpen} uiString={editing} onClose={closeDialog} />
    </Page>
  );
}
